import { Link, useNavigate } from 'react-router-dom'
import { ExternalLink } from 'lucide-react'
import { Badge } from '@/shared/components/ui/Badge'
import { formatINR } from '@/shared/utils/currency'
import { formatIndianDateTime } from '@/shared/utils/dateTime'
import type { DealDocument, DealSchedule } from './types'
import { quotationViewPath } from '@/components/sales/DealQuotationLink'

const DOC_STATUS_TONE: Record<string, string> = {
  approved: 'success',
  sent: 'info',
  draft: 'neutral',
  pending: 'warning',
  rejected: 'danger',
  revision_requested: 'warning',
  paid: 'success',
  partially_paid: 'warning',
  invoice_generated: 'info',
  cancelled: 'danger',
}

const badgeTone = (status?: string) => DOC_STATUS_TONE[String(status || '').toLowerCase()] || 'neutral'

function Row({ cells, head, onClick, style }: { cells: React.ReactNode[]; head?: boolean; onClick?: () => void; style?: React.CSSProperties }) {
  const Cell = head ? 'th' : 'td'
  return (
    <tr
      className={head ? 'border-b border-border/60 text-left text-[11px] uppercase tracking-wide text-muted-foreground' : 'border-b border-border/40 last:border-0'}
      onClick={onClick}
      style={style}
    >
      {cells.map((cell, i) => (
        <Cell key={i} className={head ? 'py-2 pr-3 font-semibold' : 'py-2.5 pr-3 text-sm text-foreground'}>
          {cell}
        </Cell>
      ))}
    </tr>
  )
}

function DocumentTable({
  docs,
  heading,
  href,
  empty,
}: {
  docs: DealDocument[]
  heading: string
  href: string
  empty: string
}) {
  const navigate = useNavigate()
  const isQuotationTab = heading === 'Quotations'

  const handleRowClick = (doc: DealDocument) => {
    if (isQuotationTab) {
      navigate(quotationViewPath({ id: doc._id }))
    }
  }

  return (
    <div>
      <div className="mb-2 flex items-center justify-between">
        <h4 className="text-sm font-bold text-foreground">{heading}</h4>
        <Link to={href} className="inline-flex items-center gap-1 text-xs font-semibold text-primary hover:underline">
          Open {heading} <ExternalLink className="h-3 w-3" />
        </Link>
      </div>
      {docs.length === 0 ? (
        <p className="rounded-lg border border-dashed border-border/60 bg-muted/10 px-4 py-6 text-center text-xs text-muted-foreground">
          {empty}
        </p>
      ) : (
        <table className="w-full">
          <thead>
            <Row head cells={['Number', 'Date', 'Amount', 'Status']} />
          </thead>
          <tbody>
            {docs.map((d) => (
              <Row
                key={d._id}
                onClick={() => handleRowClick(d)}
                style={isQuotationTab ? { cursor: 'pointer' } : undefined}
                cells={[
                  <span className="font-mono text-xs">{d.number || '—'}</span>,
                  <span className="text-xs text-muted-foreground">
                    {formatIndianDateTime(d.date || d.createdAt, { timeStyle: undefined }) || '—'}
                  </span>,
                  <span className="font-semibold">{d.total ? formatINR(d.total) : '—'}</span>,
                  <Badge tone={badgeTone(d.status)} size="sm">
                    {d.status}
                  </Badge>,
                ]}
              />
            ))}
          </tbody>
        </table>
      )}
    </div>
  )
}

function ScheduleTable({ schedules }: { schedules: DealSchedule[] }) {
  const rows = schedules.flatMap((s) => s.milestones.map((m) => ({ s, m })))
  if (rows.length === 0) {
    return (
      <p className="rounded-lg border border-dashed border-border/60 bg-muted/10 px-4 py-6 text-center text-xs text-muted-foreground">
        No payment schedule has been raised for this deal yet.
      </p>
    )
  }
  return (
    <table className="w-full">
      <thead>
        <Row head cells={['Stage', 'Quotation', '%', 'Amount', 'Received', 'Status']} />
      </thead>
      <tbody>
        {rows.map(({ s, m }) => (
          <Row
            key={`${s._id}-${m.milestoneNumber}`}
            cells={[
              <span className="font-semibold">{m.name || `Stage ${m.milestoneNumber}`}</span>,
              <span className="font-mono text-xs">{s.quotationNumber || '—'}</span>,
              <span className="text-xs text-muted-foreground">{m.percentage ? `${m.percentage}%` : '—'}</span>,
              <span>{m.milestoneTotal ? formatINR(m.milestoneTotal) : '—'}</span>,
              <span>{m.amountReceived ? formatINR(m.amountReceived) : '—'}</span>,
              <Badge tone={badgeTone(m.status)} size="sm">
                {String(m.status || '').replace(/_/g, ' ')}
              </Badge>,
            ]}
          />
        ))}
      </tbody>
    </table>
  )
}

function Field({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div>
      <p className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">{label}</p>
      <p className="mt-0.5 text-sm text-foreground">{value || '—'}</p>
    </div>
  )
}

export function OverviewTab({ detail }: { detail: any }) {
  const deal = detail?.deal || {}
  const lead = detail?.lead
  return (
    <div className="space-y-5">
      <div className="grid grid-cols-2 gap-4 md:grid-cols-3">
        <Field label="Client" value={deal.client} />
        <Field label="Stage" value={deal.stage} />
        <Field label="Priority" value={deal.priority} />
        <Field label="Deal value" value={deal.value ? formatINR(deal.value) : ''} />
        <Field label="Probability" value={deal.probability != null ? `${deal.probability}%` : ''} />
        <Field label="Expected close" value={deal.closeDate} />
        <Field label="Source" value={deal.source} />
        <Field label="Owner" value={deal.owner} />
        <Field label="Created" value={formatIndianDateTime(deal.createdAt)} />
      </div>
      <div className="grid grid-cols-2 gap-4 md:grid-cols-3">
        <Field label="Contact" value={deal.contactPerson} />
        <Field label="Designation" value={deal.contactDesignation} />
        <Field label="Email" value={deal.contactEmail} />
        <Field label="Phone" value={deal.contactPhone} />
        <Field label="GSTIN" value={deal.clientGstin || deal.gstin} />
        <Field label="Place of supply" value={deal.clientState} />
      </div>
      {Array.isArray(deal.services) && deal.services.length > 0 && (
        <div>
          <p className="mb-1.5 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">Services</p>
          <div className="flex flex-wrap gap-1.5">
            {deal.services.map((s: string) => (
              <Badge key={s} tone="neutral" size="sm">
                {s}
              </Badge>
            ))}
          </div>
        </div>
      )}
      {deal.notes && <Field label="Notes" value={<span className="whitespace-pre-wrap">{deal.notes}</span>} />}
      {lead && (
        <div className="flex items-center justify-between rounded-xl border border-border/60 bg-muted/20 px-4 py-3">
          <div>
            <p className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">Source lead</p>
            <p className="text-sm font-semibold text-foreground">{lead.name}</p>
            <p className="text-xs text-muted-foreground">
              {lead.source ? `${lead.source} · ` : ''}
              {lead.status || ''}
            </p>
          </div>
          <Link to={`/app/leads?view=${lead.id}`} className="text-xs font-semibold text-primary hover:underline">
            Open lead
          </Link>
        </div>
      )}
    </div>
  )
}

export function DocumentsTabs({ detail, tab }: { detail: any; tab: string }) {
  const documents: DealDocument[] = detail?.documents || []
  const schedules: DealSchedule[] = detail?.schedules || []
  const of = (type: string) => documents.filter((d) => d.requestType === type)

  if (tab === 'overview') return <OverviewTab detail={detail} />
  if (tab === 'quotations')
    return <DocumentTable docs={of('quotation')} heading="Quotations" href="/app/sales/quotations" empty="No quotation has been raised on this deal." />
  if (tab === 'proforma')
    return <DocumentTable docs={of('proforma')} heading="Proforma invoices" href="/app/sales/proforma-invoices" empty="No proforma invoice has been issued on this deal." />
  if (tab === 'payment') return <ScheduleTable schedules={schedules} />
  return <DocumentTable docs={of('invoice')} heading="Tax invoices" href="/app/sales/tax-invoices" empty="No tax invoice has been issued on this deal." />
}
